import React from "react";
import { type IntegrationSimple, type CaseStudySimple } from "lib/sanity";
import { Section, sectionSpacingY } from "components/section";
import {
  SecurityFeatures,
  ComposableCdpAccordion,
  IndustryLeaderAwards,
  DestinationMarquee,
  CustomerStats,
  CaseStudySlider,
} from "components/content";
import { AidAccordion, ComposableCdpSlideshow, PlatformCards, UseCasesByTeam } from "components/home";
import { SolidPlate } from "ui/plates";
import { getSafeCaseStudy } from "utils";
import { DecorativeBackgroundImage } from "components/images";
import { SliderVideo } from "./slider-video";
import { AgentsVideoSection } from "./agents-video-section";

const petsmartShort = "https://cdn.sanity.io/files/pwmfmi47/production/10756e213114aba50eb44269c201efc0be27f900.mp4";
const petsmartVideo = "https://cdn.sanity.io/files/pwmfmi47/production/d32cfe274623d8147658ece7363c7b838d00b374.mp4";
const petsmartCaptions = "/assets/home/petsmart-captions.vtt";
const petsmartThumbnail =
  "https://cdn.sanity.io/images/pwmfmi47/production/428b3051d614b60006c29dd8f70e3e59bc7df850-2108x1188.webp";

const fundriseShort = "https://cdn.sanity.io/files/pwmfmi47/production/51d018caee40e22465d1e6fbff0e511ed63d5506.mp4";
const fundriseVideo = "https://cdn.sanity.io/files/pwmfmi47/production/f7212888480ea02e57347cc81fbbbd1fbe420636.mp4";
const fundriseThumbnail =
  "https://cdn.sanity.io/images/pwmfmi47/production/9eea29e146b357ecb97a0970a0ddbb73a2109683-3010x1682.webp";
const fundriseCaptions = "/assets/home/fundrise-captions.vtt";

type HomeTemplateProps = {
  destinations: IntegrationSimple[];
  caseStudies: CaseStudySimple[];
};

export function HomeTemplate({ destinations, caseStudies }: HomeTemplateProps) {
  const petsmart = getSafeCaseStudy(caseStudies, "petsmart");
  const wmg = getSafeCaseStudy(caseStudies, "wmg");
  const fundrise = getSafeCaseStudy(caseStudies, "fundrise");
  const chime = getSafeCaseStudy(caseStudies, "chime");
  const whoop = getSafeCaseStudy(caseStudies, "whoop");
  const weightWatchers = getSafeCaseStudy(caseStudies, "weight-watchers");

  return (
    <>
      <PlatformCards />

      <CustomerStats customers={[wmg.customer, petsmart.customer, weightWatchers.customer]} />

      <CaseStudySlider
        my={0}
        pb={sectionSpacingY}
        title="Leaders are re-inventing marketing with their data. You can too."
        slides={[
          {
            title: whoop.title,
            caseStudy: whoop,
            media: <DecorativeBackgroundImage src={whoop.fullImageUrl} />,
            url: `/customers/${whoop.customer.slug}`,
          },
          {
            title: "Warner Music Group leverages Hightouch to “super-serve” fans, artists, and labels",
            caseStudy: wmg,
            media: <DecorativeBackgroundImage src={wmg.fullImageUrl} />,
            url: `/customers/${wmg.customer.slug}`,
          },
          {
            title: "PetSmart personalizes journeys for over 70 million loyalty program members",
            caseStudy: petsmart,
            media: (
              <SliderVideo
                shortSrc={petsmartShort}
                src={petsmartVideo}
                captions={petsmartCaptions}
                thumbnail={petsmartThumbnail}
                uploadDate="Feb 10, 2025"
                videoId="petsmart-case-study"
                title="PetSmart powers personalization for 70M+ loyalty members and drives double-digit growth"
                description="Bradley Breuer, VP of Marketing at PetSmart, discusses how the company is delivering personalized experiences to millions of pet-owners using Composable CDP powered by Hightouch."
              />
            ),
            url: `/customers/${petsmart.customer.slug}`,
          },
          {
            title: chime.title,
            caseStudy: chime,
            media: <DecorativeBackgroundImage src={chime.fullImageUrl} />,
            url: `/customers/${chime.customer.slug}`,
          },
          {
            title: fundrise.title,
            caseStudy: fundrise,
            media: (
              <SliderVideo
                shortSrc={fundriseShort}
                src={fundriseVideo}
                captions={fundriseCaptions}
                thumbnail={fundriseThumbnail}
                uploadDate="August 4, 2025"
                videoId="fundrise-case-study"
                title={fundrise.title}
                description="Jon Carden, CMO at Fundrise, shows how the marketing team uses AI agents to scale 1:1 personalization and drive 4x increase in investment growth."
              />
            ),
            url: `/customers/${fundrise.customer.slug}`,
          },
        ]}
      />

      <SolidPlate
        bgColor="new.blue.950"
        spotlightColor="new.blue.900"
        theme="dark"
        maxW="100%"
        py={{ base: "bigger", md: "biggest" }}
        px={0}
      >
        <AgentsVideoSection />
        <ComposableCdpSlideshow />
        <AidAccordion />
      </SolidPlate>

      <Section my="0" pt={sectionSpacingY}>
        <ComposableCdpAccordion />
      </Section>

      <SecurityFeatures mt={{ base: "biggest", md: "huger" }} mb={{ base: "biggest", md: "huge" }} />

      <UseCasesByTeam />

      <IndustryLeaderAwards />

      <DestinationMarquee destinations={destinations} my={0} py={sectionSpacingY} />
    </>
  );
}
